/**
 * Inventory Store - Zustand med Cosmos DB-synk
 * 
 * Hanterar beredskapslager/förråd med föremål.
 * Synkroniserar med backend API och fungerar offline med localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth-store';
import { apiGet, apiPut, apiPost, apiDelete } from '@/services/api-client';
import { logActivity } from '@/lib/activity-logger';

const INVENTORY_REVIEW_INTERVAL_DAYS = 180;

// ============================================================================
// Types
// ============================================================================

export type InventoryCategory = 
  | 'mat'
  | 'medicin'
  | 'hygien'
  | 'verktyg'
  | 'kommunikation'
  | 'varme'
  | 'ljus'
  | 'dokument'
  | 'ovrigt';

export interface InventoryItem {
  id: string;
  name: string;
  category: InventoryCategory;
  quantity: number;
  unit: string;
  expirationDate?: string | null;
  // Allow explicit null when clearing an expiration date from the UI
  goal?: number;
  // (backend accepts null to clear the field)
  location: string;
  notes?: string | null;
  link?: string | null;
  kcalPerUnit?: number; // Endast för mat-kategorin
  lastInventoryDate?: string; // Senast inventerad
  createdAt: string;
  updatedAt: string;
}

export interface InventoryStats {
  totalItems: number;
  totalQuantity: number;
  expiredCount: number;
  expiringCount: number;
  uninventoriedCount: number;
  byCategory: Record<string, number>;
}

interface InventoryState {
  // Data
  items: InventoryItem[];
  stats: InventoryStats;
  
  // Synk-status
  lastSyncAt: string | null;
  hasPendingChanges: boolean;
  isSyncing: boolean;
  syncError: string | null;
  
  // Actions
  addItem: (item: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>, familyId?: string) => void;
  updateItem: (id: string, updates: Partial<InventoryItem>, familyId?: string) => void;
  deleteItem: (id: string, familyId?: string) => void;
  
  // Synk
  fetchFromServer: (familyId?: string) => Promise<void>;
  syncToServer: () => Promise<void>;
  
  // Internal
  _recalculateStats: () => void;
  _addItemToServer: (item: InventoryItem, familyIdOverride?: string) => Promise<void>;
  _updateItemOnServer: (id: string, updates: Partial<InventoryItem>, familyIdOverride?: string) => Promise<void>;
  _deleteItemOnServer: (id: string, familyIdOverride?: string) => Promise<void>;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// API URL
// (Använder api-client.ts med automatisk token refresh)

// Kategori-översättningar
export const CATEGORY_LABELS: Record<InventoryCategory, string> = {
  mat: 'Mat & Dryck',
  medicin: 'Medicin & Sjukvård',
  hygien: 'Hygien',
  verktyg: 'Utrustning',
  kommunikation: 'Kommunikation',
  varme: 'Värme & Bränsle',
  ljus: 'Energi & Belysning',
  dokument: 'Viktiga dokument',
  ovrigt: 'Övrigt',
};

// ============================================================================
// Store
// ============================================================================

export const useInventoryStore = create<InventoryState>()(
  persist(
    (set, get) => ({
      // Default values
      items: [],
      stats: {
        totalItems: 0,
        totalQuantity: 0,
        expiredCount: 0,
        expiringCount: 0,
        uninventoriedCount: 0,
        byCategory: {},
      },
      lastSyncAt: null,
      hasPendingChanges: false,
      isSyncing: false,
      syncError: null,
      _hasHydrated: false,

      addItem: (itemData, familyId?: string) => {
        const now = new Date().toISOString();
        const newItem: InventoryItem = {
          ...itemData,
          id: Date.now().toString(),
          lastInventoryDate: itemData.lastInventoryDate || now,
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          items: [...state.items, newItem],
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._addItemToServer(newItem, familyId);
      },

      updateItem: (id, updates, familyId?: string) => {
        const now = new Date().toISOString();
        set((state) => ({
          items: state.items.map(item =>
            item.id === id ? { ...item, ...updates, updatedAt: now } : item
          ),
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._updateItemOnServer(id, updates, familyId);
      },

      deleteItem: (id, familyId?: string) => {
        set((state) => ({
          items: state.items.filter(item => item.id !== id),
          hasPendingChanges: true,
        }));
        get()._recalculateStats();
        get()._deleteItemOnServer(id, familyId);
      },

      _recalculateStats: () => {
        const { items } = get();
        
        const now = new Date();
        const oneMonthFromNow = new Date();
        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
        
        const expiredItems = items.filter(item => {
          if (!item.expirationDate) return false;
          return new Date(item.expirationDate) < now;
        });

        const expiringItems = items.filter(item => {
          if (!item.expirationDate) return false;
          const expDate = new Date(item.expirationDate);
          return expDate >= now && expDate <= oneMonthFromNow;
        });

        const uninventoriedThreshold = new Date();
        uninventoriedThreshold.setDate(uninventoriedThreshold.getDate() - INVENTORY_REVIEW_INTERVAL_DAYS);
        const uninventoriedItems = items.filter(item => {
          if (!item.lastInventoryDate) return false;
          return new Date(item.lastInventoryDate) < uninventoriedThreshold;
        });

        const byCategory: Record<string, number> = {};
        for (const item of items) {
          byCategory[item.category] = (byCategory[item.category] || 0) + 1;
        }

        set({
          stats: {
            totalItems: items.length,
            totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
            expiredCount: expiredItems.length,
            expiringCount: expiringItems.length,
            uninventoriedCount: uninventoriedItems.length,
            byCategory,
          },
        });
      },

      _addItemToServer: async (item, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const familyId = familyIdOverride || ownerId || useAuthStore.getState().user?.familyId;
        const body = familyId ? { ...item, familyId } : item;
        const result = await apiPost<{ item: InventoryItem }>(`/inventory${familyId ? `?familyId=${encodeURIComponent(familyId)}` : ''}`, body);
        if (result.success && result.data?.item?.id) {
          logActivity({ action: 'ADD_INVENTORY_ITEM', category: 'inventory', description: 'Item added', metadata: { itemId: result.data.item.id, name: item.name } });
          set((state) => ({
            items: state.items.map(i =>
              i.id === item.id ? { ...i, id: result.data!.item.id } : i
            ),
          }));
        }
      },

      _updateItemOnServer: async (id, updates, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId2 = getEffectiveFamilyOwnerId();
        const familyId2 = familyIdOverride || ownerId2 || useAuthStore.getState().user?.familyId;
        const qs = familyId2 ? `?familyId=${encodeURIComponent(familyId2)}` : '';
        const result = await apiPut(`/inventory/${id}${qs}`, updates);
        if (result.success) {
          logActivity({ action: 'UPDATE_INVENTORY_ITEM', category: 'inventory', description: 'Item updated', metadata: { itemId: id } });
        }
      },

      _deleteItemOnServer: async (id, familyIdOverride?: string) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId3 = getEffectiveFamilyOwnerId();
        const familyId3 = familyIdOverride || ownerId3 || useAuthStore.getState().user?.familyId;
        const qs = familyId3 ? `?familyId=${encodeURIComponent(familyId3)}` : '';
        const result = await apiDelete(`/inventory/${id}${qs}`);
        if (result.success) {
          logActivity({ action: 'DELETE_INVENTORY_ITEM', category: 'inventory', description: 'Item deleted', metadata: { itemId: id } });
        }
      },

      fetchFromServer: async (familyId?: string) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          // Inte inloggad - använd lokal data
          return;
        }

        set({ isSyncing: true, syncError: null });

        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId4 = getEffectiveFamilyOwnerId();
          const resolvedFamilyId = familyId || ownerId4 || useAuthStore.getState().user?.familyId;
          const qs = resolvedFamilyId ? `?familyId=${encodeURIComponent(resolvedFamilyId)}` : '';
          const result = await apiGet<{
            items: InventoryItem[];
            stats?: InventoryStats;
          }>(`/inventory${qs}`);

          if (result.success && result.data) {
            set({
              items: result.data.items || [],
              isSyncing: false,
              hasPendingChanges: false,
              lastSyncAt: new Date().toISOString(),
            });
            get()._recalculateStats();
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.warn('Failed to fetch inventory:', error);
          set({
            isSyncing: false,
            syncError: 'Failed to fetch inventory',
          });
        }
      },

      syncToServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;

        const { items } = get();
        set({ isSyncing: true, syncError: null });

        try {
          const result = await apiPost<{
            items: InventoryItem[];
            syncedAt?: string;
          }>('/inventory/sync', { items });

          if (result.success && result.data) {
            set({
              items: result.data.items || items,
              isSyncing: false,
              hasPendingChanges: false,
              lastSyncAt: result.data.syncedAt || new Date().toISOString(),
            });
            get()._recalculateStats();
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.warn('Failed to sync inventory:', error);
          set({
            isSyncing: false,
            syncError: 'Failed to sync inventory',
          });
        }
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
        get()._recalculateStats();
      },
    }),
    {
      name: 'beredskapshubben-inventory',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        lastSyncAt: state.lastSyncAt,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// ============================================================================
// Hooks
// ============================================================================

export const useInventoryHydrated = () => useInventoryStore((state) => state._hasHydrated);

// Helper för att få items per kategori
export const useItemsByCategory = (category: InventoryCategory) => 
  useInventoryStore((state) => state.items.filter(item => item.category === category));

// Helper för att få items som snart går ut
export const useExpiringItems = () => {
  const items = useInventoryStore((state) => state.items);
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  const now = new Date();
  
  return items.filter(item => {
    if (!item.expirationDate) return false;
    const expDate = new Date(item.expirationDate);
    return expDate >= now && expDate <= oneMonthFromNow;
  });
};

// Helper för att få utgångna items
export const useExpiredItems = () => {
  const items = useInventoryStore((state) => state.items);
  const now = new Date();
  
  return items.filter(item => {
    if (!item.expirationDate) return false;
    return new Date(item.expirationDate) < now;
  });
};
